import { useCallback } from "react";
import useStore from "../helpers/useStore";
import {
  MutationDraftContext,
  useMutationScope,
} from "../stores/MutationDraft/mutationDraft.provider";
import { MutationDraft, MutationRequest, MutationScope } from "../stores/MutationDraft/types";
import { useCalendarMutationGenerator } from "./useCalendarMutationGenerator";

/**
 * Adapter that bridges the mutation generator and the draft store.
 *
 * Takes a MutationRequest, calls the generator to produce a MutationPlan
 * and stores the result in CalendarAppointmentMutationDraftStore
 * so the calendar can render it.
 *
 * Scope is resolved from MutationScopeContext if not passed explicitly.
 *
 * When the backend returns mutations directly, callers can use
 * `adaptAndStore(request, backendDraft)` to skip the local generator.
 */
export const useCalendarMutationAdapter = (scopeOverride?: MutationScope) => {
  const contextScope = useMutationScope();
  const scope = scopeOverride ?? contextScope;

  const registry = useStore(MutationDraftContext);
  const draftStore = registry.getStore(scope);
  const { generatePlacement, shiftExistingAndPlace, isReady } = useCalendarMutationGenerator();

  /**
   * Generate a mutation from a request and store it as a draft.
   * Tries shift-and-place first, falls back to simple gap placement.
   * Returns the produced draft or null if no placement was possible.
   */
  const generateAndStore = useCallback(
    (request: MutationRequest): MutationDraft | null => {
      // Try shift-and-place first (produces richer plans)
      const plan = shiftExistingAndPlace(request);
      if (plan) {
        draftStore.setDraft(request.id, plan, request);
        return plan;
      }

      // Fallback: find an existing gap
      const simple = generatePlacement(request);
      if (simple) {
        draftStore.setDraft(request.id, simple, request);
        return simple;
      }

      return null;
    },
    [shiftExistingAndPlace, generatePlacement, draftStore],
  );

  /**
   * Store a pre-computed draft (e.g. from backend) without running the generator.
   * Use this when the backend already provides mutations.
   */
  const adaptAndStore = useCallback(
    (request: MutationRequest, draft: MutationDraft): void => {
      draftStore.setDraft(request.id, draft, request);
    },
    [draftStore],
  );

  /**
   * Regenerate a draft by re-running the generator for the original request.
   * Returns the new draft or null.
   */
  const regenerate = useCallback(
    (draftId: string): MutationDraft | null => {
      const existingRequest = draftStore.getRequest(draftId);
      if (!existingRequest) return null;

      // Remove old draft + rejection (entry may already be gone from reject)
      draftStore.removeDraft(draftId);
      draftStore.clearRejection(draftId);

      // Regenerate — this calls generateAndStore which does setDraft
      const newDraft = generateAndStore(existingRequest);
      return newDraft;
    },
    [draftStore, generateAndStore],
  );

  return {
    /** Generate + store a draft from a request */
    generateAndStore,
    /** Store a pre-computed draft (from backend) */
    adaptAndStore,
    /** Regenerate an existing draft */
    regenerate,
    /** Whether the generator is ready */
    isReady,
  };
};
